import { Button } from "@progress/kendo-react-buttons";
import { Field, Form, FormElement } from "@progress/kendo-react-form";
import { Grid, GridColumn, GridToolbar, GridCell } from "@progress/kendo-react-grid";
import { FormDatePicker, FormDropDownList, FormNumericTextBox } from "commons/forms/form";
import { dateFormatDay } from "commons/models/dates";
import MediaCardTemplate from "pages/gant/components/factories/MediaCardTemplate";
import campCell from "pages/tables/components/campCell";
import PtrCell from "pages/tables/components/ptrCell";
import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { process } from '@progress/kendo-data-query';
import { getGroupIds, setExpandedState, setGroupIds } from "@progress/kendo-react-data-tools";
import * as list from '../../../commons/models/list';
import { required } from "commons/forms/validators";
import LoaderContainer from "commons/container/LoaderContainer";
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { useGetAchatsMediasQuery, useGetMediasQuery } from "../../../redux/api/cache";

export default function AchatsMedias() {
    const _export = useRef(null);
    const excelExport = () => {
        if (_export.current !== null) {
            _export.current.save();
        }
    };
    const [totalProduct, setTotalProduct] = useState(0);
    const [params, setParams] = useState("");
    const [collapsedState, setCollapsedState] = useState([]);
    const { data: medias } = useGetMediasQuery();
    const { data: data, isLoading, isFetching } = useGetAchatsMediasQuery(params, { skip: !params });

    useEffect(() => {
        const listener = event => {
            if (event.code === "Enter" || event.code === "NumpadEnter") {
                document.querySelector('button[type="submit"]').click();
            }
        };
        document.addEventListener("keydown", listener);

        return () => {
            document.removeEventListener("keydown", listener);
        };
    }, []);

    let groupAchatsMedias = [];

    if (localStorage.getItem('group_achats_medias') !== null) {
        let temp = localStorage.getItem('group_achats_medias').split('-');

        temp.forEach((elem) => {
            if (elem !== '') {
                groupAchatsMedias.push({ field: elem });
            }
        });
    }

    const initialDataState = {
        group: groupAchatsMedias.length === 0 ? [] : groupAchatsMedias,
    };

    const processWithGroups = (data, dataState) => {
        let temp = 0;
        const newDataState = process(data, dataState);

        if (dataState.group.length !== 0) {
            newDataState.data.map((elem) => {
                temp = elem.items?.length;

                if (dataState.group.length === 2) {
                    temp = 0;
                    elem.items.map((item) => {
                        item.value = item.value + ' (' + item.items?.length + ')';
                        temp += item.items?.length;
                    });
                }

                elem.value = elem.value + ' (' + temp + ')';
            });
        }

        setGroupIds({
            data: newDataState.data,
            group: dataState.group,
        });
        return newDataState;
    };

    const [dataState, setDataState] = React.useState(initialDataState);
    const [resultState, setResultState] = useState(
        processWithGroups([], initialDataState)
    );

    const updateData = () => {
        setResultState(processWithGroups(data.data, dataState));
        setTotalProduct(data.data.length);
    };

    useEffect(() => {
        if (data)
            updateData();
    }, [data]);

    useEffect(() => {
        if (data) {
            updateData();
            let tempGroup = '';
            dataState.group.forEach((element) => {
                tempGroup += element.field + '-';
            });

            localStorage.setItem('group_rapport_coop', tempGroup);
        }
    }, [dataState]);

    const onDataStateChange = React.useCallback(async (event) => {
        if (event.dataState.group.length < 3)
            setDataState(event.dataState);
    }, []);

    const onExpandChange = useCallback(
        (event) => {
            const item = event.dataItem;
            if (item.groupId) {
                const newCollapsedIds = !event.value
                    ? [...collapsedState, item.groupId]
                    : collapsedState.filter((groupId) => groupId !== item.groupId);
                setCollapsedState(newCollapsedIds);
            }
        },
        [collapsedState]
    );

    const newData = setExpandedState({
        data: resultState.data,
        collapsedIds: collapsedState,
    });

    const update = async (dataItem) => {
        let params = "";
        params += dataItem['dateDebut'] ? `dt_deb=${dateFormatDay(dataItem.dateDebut)}&` : "";
        params += dataItem['dateFin'] ? `dt_fin=${dateFormatDay(dataItem.dateFin)}&` : "";
        params += dataItem['camp_seq'] ? `camp_num=${dataItem['camp_seq']}&` : "";

        if (
            dataItem['media'] &&
            dataItem['media'] !== 'Tous'
        ) {
            params += `media=${medias.data.find((elem) => elem.media_descr === dataItem['media'])
                .media_cd
                }&`;
        }

        if (params.includes('camp_num'))
            setParams(params);
    };

    const onGroupsToggle = useCallback(() => {
        setCollapsedState(
            collapsedState.length
                ? []
                : getGroupIds({
                    data: newData,
                })
        );
    }, [collapsedState, newData]);

    const columnNames = useMemo(
        () =>
            data !== undefined ? list.getColumnNames(data?.meta.definition) : '',
        [data?.meta.definition]
    );

    const numeroCampagneValidator = (e) => required(e, 'Numéro de campagne');

    return (
        <MediaCardTemplate
            isLoaded={true}
            exp={true}
            card={{ label: 'Achats médias' }}
            data={newData}
        >
            <Form
                onSubmit={update}
                render={() => (
                    <FormElement>
                        <div className='row'>
                            <div className='col-md-2'>
                                <Field
                                    id={'dateDebut'}
                                    name={'dateDebut'}
                                    label="Date de début"
                                    component={FormDatePicker}
                                />
                            </div>
                            <div className='col-md-2'>
                                <Field
                                    id={'dateFin'}
                                    name={'dateFin'}
                                    label="Date de fin"
                                    component={FormDatePicker}
                                />
                            </div>
                            <div className='col-md-2'>
                                <Field
                                    id={'camp_seq'}
                                    name={'camp_seq'}
                                    label="Numéro de campagne"
                                    component={FormNumericTextBox}
                                    validator={numeroCampagneValidator}
                                />
                            </div>
                            <div className='col-md-2'>
                                <Field
                                    id={'media'}
                                    name={'media'}
                                    label="Média"
                                    defaultItem={'Tous'}
                                    data={medias?.data?.map((item) => item.media_descr)}
                                    component={FormDropDownList}
                                />
                            </div>
                        </div>
                        <div className='inline'>
                            <div style={{ margin: '15px 0 15px 0' }}>
                                <Button className='blueButton' type={'submit'}>
                                    Rechercher
                                </Button>
                            </div>
                        </div>
                    </FormElement>
                )}
            />
            <span style={{ marginTop: '5px', color: '#a5a5a5', fontWeight: 'bold' }}>
                {totalProduct === 0
                    ? 'Aucune donnée disponible'
                    : totalProduct + ' produits trouvés'}
            </span>
            {newData !== undefined && newData.length !== 0 && (
                <div>
                    {!isLoading && !isFetching && (<ExcelExport fileName="Export-Achats-medias" data={newData} ref={_export}><Grid
                        style={{
                            height: '850px',
                        }}
                        groupable={true}
                        sortable={true}
                        data={newData}
                        {...dataState}
                        onDataStateChange={onDataStateChange}
                        onExpandChange={onExpandChange}
                        expandField='expanded'
                        resizable
                    >
                        <GridToolbar>
                            <Button
                                title="Exporter le tableau au format Excel"
                                className="blueButton"
                                onClick={excelExport}>Export au format Excel
                            </Button>
                            {dataState.group && dataState.group.length !== 0 && (<Button className='whiteButton' onClick={onGroupsToggle}>
                                {collapsedState.length ? 'Étendre' : 'Réduire'}
                            </Button>)}
                        </GridToolbar>
                        {columnNames.map((elem, i) => (
                            <GridColumn
                                field={elem['field']}
                                key={i}
                                title={elem['label']}
                                cell={elem['field'] === 'prod_num' ? PtrCell
                                    : elem['field'] === 'camp_descr' ? campCell
                                        : GridCell}
                                {
                                ...list.calculateWidth(
                                    elem,
                                    i === data?.meta.definition.length - 1,
                                    newData,
                                    true
                                )
                                }
                            />
                        ))}
                    </Grid></ExcelExport>)}
                </div>
            )}
            {(isFetching || isLoading) && (<LoaderContainer />)}
        </MediaCardTemplate>
    );
}